import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { scrollToSection } from '../utils/scrollToSection';

export interface NavSection {
  id: string;
  label: string;
}

interface Props {
  sections: NavSection[];
  // Height of whatever sticks above the content (the sticky header), so jumps and the active
  // marker both account for it. Return 0 when nothing is sticky.
  getOffset: () => number;
}

// Horizontal "on this page" pill row with a scroll-back / scroll-forward chevron at each end
// (the Figma "Quick navigation" strip). Highlights the section currently under the sticky
// header, keeps that pill in view, and scrolls to a section on click. Sections that are not
// rendered are simply not passed in, so the row only ever lists what is on the page.
const SectionNav: React.FC<Props> = ({ sections, getOffset }) => {
  const [active, setActive] = useState<string | null>(null);
  const idsKey = sections.map((s) => s.id).join('|');

  useEffect(() => {
    const ids = idsKey ? idsKey.split('|') : [];
    const update = () => {
      const line = getOffset() + 24;
      let current: string | null = ids[0] ?? null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - line <= 0) current = id;
        else break;
      }
      // At the very bottom of the page the last section may never reach the line; mark it.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2 && ids.length) {
        current = ids[ids.length - 1];
      }
      setActive(current);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [idsKey, getOffset]);

  // The pill row scrolls sideways; the chevrons page it and go quiet at either end.
  const rowRef = useRef<HTMLDivElement>(null);
  const [canBack, setCanBack] = useState(false);
  const [canForward, setCanForward] = useState(false);
  const updateEdges = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setCanBack(el.scrollLeft > 1);
    setCanForward(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateEdges) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
      ro?.disconnect();
    };
  }, [updateEdges, idsKey]);
  const page = (dir: -1 | 1) => {
    const el = rowRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, Math.round(el.clientWidth * 0.6)), behavior: 'smooth' });
  };

  // Keep the active pill visible as the reader moves through the page. Scrolls the row only,
  // never the page (scrollIntoView could move the page on small screens where nothing sticks).
  useEffect(() => {
    const el = rowRef.current;
    if (!el || !active) return;
    const btn = el.querySelector<HTMLElement>(`[data-section="${active}"]`);
    if (!btn) return;
    const left = btn.offsetLeft;
    const right = left + btn.offsetWidth;
    if (left < el.scrollLeft) el.scrollTo({ left: Math.max(0, left - 8), behavior: 'smooth' });
    else if (right > el.scrollLeft + el.clientWidth) el.scrollTo({ left: right - el.clientWidth + 8, behavior: 'smooth' });
  }, [active]);

  const jump = useCallback((id: string) => { scrollToSection(id, getOffset()); }, [getOffset]);

  if (sections.length < 2) return null;

  const chevron = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-35 disabled:hover:bg-transparent';

  return (
    <nav aria-label="On this page" className="flex items-center gap-0.5 py-2.5">
      <button type="button" onClick={() => page(-1)} disabled={!canBack} aria-label="Earlier sections" className={chevron}>
        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      <div ref={rowRef} className="relative flex-1 min-w-0 flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {sections.map((s) => {
          const isActive = s.id === active;
          return (
            <button
              key={s.id}
              type="button"
              data-section={s.id}
              onClick={() => jump(s.id)}
              aria-current={isActive ? 'location' : undefined}
              className={`shrink-0 rounded-3xl px-4 py-2 text-sm font-medium leading-5 whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-gray-700 text-white dark:bg-gray-100 dark:text-gray-900'
                  : 'text-gray-700 hover:bg-gray-300 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      <button type="button" onClick={() => page(1)} disabled={!canForward} aria-label="Later sections" className={chevron}>
        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </nav>
  );
};

export default SectionNav;
